import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Progress,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  GithubOutlined,
  GiftOutlined,
  GlobalOutlined,
  KeyOutlined,
  LinkOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import '../styles/dashboard.css';
import { useTimezone } from '../contexts/TimezoneContext';

const { Title, Text } = Typography;

interface AccountStats {
  total: number;
  // 一个号绑两站算两项。下面除 total / accounts_* 之外的计数都是这个维度，
  // 算比例必须用 site_links 当分母。
  site_links?: number;
  oauth_linked: number;
  healthy: number;
  disabled: number;
  token_invalid: number;
  missing_token: number;
  pending_checkin: number;
  checked_in_today: number;
  not_checked_in_today: number;
  unknown: number;
  // 今日签到来源分布，用于判断会话缓存是否生效。三项之和等于 checked_in_today
  checkin_via_cache?: number;
  checkin_via_solver?: number;
  checkin_via_unknown?: number;
  solver_cost_today?: number;
  login_via_protocol?: number;
  login_via_browser?: number;
  // GitHub 侧账号状态。disabled 说的是目标站封禁，跟 GitHub 封号是两件事
  github_suspended?: number;
  github_needs_relogin?: number;
  github_mismatch?: number;
  github_error?: number;
  github_unchecked?: number;
  // 还缺多少个 (账号 × 站点)
  missing_links?: number;
  // 账号维度的汇总，给写着"账号"的卡片用
  accounts_all_healthy?: number;
  accounts_with_issue?: number;
  accounts_unlinked?: number;
  // 签到的账号维度。站点维度的 checked_in_today 在双站时是账号数的两倍，
  // 那个数只能叫"签到笔数"
  accounts_pending_checkin?: number;
  accounts_checked_in_today?: number;
  // 封禁/令牌同理：都是按站发生的，一个号可能在 GoRouter 被封、在 Tabi 正常
  accounts_disabled?: number;
  accounts_token_invalid?: number;
  accounts_missing_token?: number;
}

interface RateLimitStatus {
  name?: string;
  origin: string;
  limit: number;
  raw_limit: number;
  margin: number;
  window: number;
  spent: number;
  remaining: number;
  blocked_seconds: number;
  proxy_enabled: boolean;
  exit_session?: string | null;
  rotations: number;
}

interface ProxyStatus {
  enabled: boolean;
  rotate_on_rate_limit: boolean;
  session_id: string;
  rotations: number;
  countries: string;
  apply_to_target: boolean;
}

interface WebsiteStatus {
  id: number;
  name: string;
  version?: string | null;
  registration_enabled?: boolean | null;
  password_registration_enabled?: boolean | null;
  registration_check_status: string;
  registration_checked_at?: string | null;
  registration_check_message?: string | null;
  version_check_status: string;
  version_checked_at?: string | null;
}

interface SolverStatus {
  configured: boolean;
  solver_type?: string;
  balance?: number | null;
  balance_checked_at?: string | null;
  balance_error?: string | null;
  low_balance_threshold: number;
  low_balance: boolean;
  // 读不到余额（密钥失效 / 2captcha 挂了）要和"余额健康"分开
  balance_unknown?: boolean;
}

interface SolverSpend {
  today: number;
  yesterday: number;
  by_day?: Record<string, number>;
}

// 今日签到一共拿到多少额度，以及各站分别给了多少。快照里的奖励字段每天会被
// 覆盖，这个数来自按天去重的额度账本。
interface CheckinQuotaToday {
  day: string;
  quota: number;
  amount: number;
  checkins: number;
  unknown_reward: number;
  sites: Array<{ origin: string; name: string; quota: number; amount: number; checkins: number }>;
  month: { month: string; quota: number; amount: number; checkins: number };
}

interface PushTokenStatus {
  configured: boolean;
  status: string;
  message?: string | null;
  checked_at?: string | null;
  target_base_url?: string;
  mapping_count?: number;
}

interface SiteTokenAlert {
  origin: string;
  name: string;
  links: number;
  token_invalid: number;
  pending_checkin: number;
  system_version?: string | null;
  previous_system_version?: string | null;
  version_changed_at?: string | null;
  message: string;
}

interface DashboardOverview {
  generated_at: string;
  total_balance: number;
  accounts: AccountStats;
  // 整站令牌集体失效。摊进 token_invalid 计数里的话，上次是隔了一天才从签到结果发现的
  site_token_alerts?: SiteTokenAlert[];
  solver?: SolverStatus;
  solver_spend?: SolverSpend;
  checkin_quota?: CheckinQuotaToday;
  push?: PushTokenStatus;
  rate_limits?: RateLimitStatus[];
  proxy?: ProxyStatus;
  websites: {
    total: number;
    registration_open: number;
    registration_closed: number;
    registration_unknown: number;
    items: WebsiteStatus[];
  };
  schedule: {
    last_health_run_at?: string | null;
    last_checkin_run_at?: string | null;
    last_version_run_at?: string | null;
    last_registration_run_at?: string | null;
    next_health_run_at?: string | null;
    next_checkin_run_at?: string | null;
    next_version_run_at?: string | null;
    next_registration_run_at?: string | null;
    next_run_at?: string | null;
  };
  settings: {
    health_check_interval_minutes: number;
    registration_check_interval_minutes: number;
    version_check_interval_minutes: number;
    checkin_times: string[];
    checkin_enabled?: boolean;
    timezone: string;
  };
}


const formatInterval = (minutes: number) => {
  if (minutes % 1440 === 0) return `${minutes / 1440}天`;
  if (minutes % 60 === 0) return `${minutes / 60}小时`;
  return `${minutes}分钟`;
};

// 额度是目标站的原始单位（默认 500000 = $1），几亿的数字没人能一眼读出来是多少钱。
// 所以界面一律用美元；原始额度留在签到额度页里对账。
const formatMoney = (value?: number) => {
  const number = value || 0;
  const digits = number === 0 || number >= 1 ? 2 : 4;
  return `$${number.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
};

const registrationTag = (site: WebsiteStatus) => {
  if (site.registration_check_status === 'error') {
    return (
      <Tooltip title={site.registration_check_message || '注册状态探测失败'}>
        <Tag color="red">检测失败</Tag>
      </Tooltip>
    );
  }
  if (site.registration_enabled === true) {
    return site.password_registration_enabled === false ? (
      <Tag color="green">已开放注册（仅 OAuth）</Tag>
    ) : (
      <Tag color="green">已开放注册</Tag>
    );
  }
  if (site.registration_enabled === false) return <Tag color="red">已关闭注册</Tag>;
  return <Tag>注册状态未知</Tag>;
};

const Dashboard: React.FC = () => {
  // 时间按"显示设置"里选的时区渲染（默认北京）。后端存的是 UTC，
  // 而不带偏移量的字符串会被 new Date() 当本地时间，直接用就会差 8 小时。
  const { formatDateTime } = useTimezone();
  const formatDate = (value?: string | null) => formatDateTime(value, '尚未执行');
  const navigate = useNavigate();
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadOverview = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const response = await dashboardAPI.getOverview();
      setOverview(response.data);
    } catch (error) {
      console.error('加载仪表盘概览失败', error);
      message.error('概览数据加载失败，请稍后重试');
    } finally {
      isRefresh ? setRefreshing(false) : setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  if (loading && !overview) {
    return <div className="dashboard-loading"><Spin size="large" /></div>;
  }

  if (!overview) {
    return <Empty description="暂无概览数据" />;
  }

  const { accounts, websites, schedule, settings } = overview;
  // 界面上写"账号"的地方一律用 accounts_* 这一组。healthy / disabled /
  // checked_in_today 那一组是"账号×站点"维度的：38 个号各绑 2 站全部正常时它们
  // 是 76，标成"76 个账号"就对不上账号总数，除以 total 更会算出"健康率 200%"。
  const siteLinkTotal = accounts.site_links ?? accounts.oauth_linked ?? 0;
  const accountsHealthy = accounts.accounts_all_healthy ?? 0;
  const accountHealthPercent = accounts.total
    ? Math.round((accountsHealthy / accounts.total) * 100)
    : 0;
  const githubTrouble = [
    { label: 'GitHub已封禁', count: accounts.github_suspended ?? 0 },
    { label: '需重新登录', count: accounts.github_needs_relogin ?? 0 },
    { label: '缓存账号不符', count: accounts.github_mismatch ?? 0 },
    { label: '探测异常', count: accounts.github_error ?? 0 },
  ].filter((row) => row.count > 0);
  const githubTroubleTotal = githubTrouble.reduce((sum, row) => sum + row.count, 0);
  const githubTroubleDetail = githubTroubleTotal
    ? githubTrouble.map((row) => `${row.label} ${row.count}`).join(' · ')
    : `${accounts.github_unchecked ?? 0} 个未检测`;
  const solver = overview.solver;
  // 令牌状态是巡检时探的缓存值。"从未探测"要说清楚，不能显示成"有效"。
  const pushTone = ((): { text: string; tone: string } => {
    const push = overview.push;
    if (!push?.configured) return { text: '未配置', tone: 'slate' };
    switch (push.status) {
      case 'valid': return { text: '有效', tone: 'green' };
      case 'invalid': return { text: '已失效', tone: 'red' };
      case 'unreachable': return { text: '目标不可达', tone: 'amber' };
      default: return { text: '未探测', tone: 'slate' };
    }
  })();
  const pushDetail = overview.push?.configured
    ? (overview.push.status === 'unknown' || !overview.push.checked_at
      ? '等下一轮巡检探测'
      : `${overview.push.mapping_count ?? 0} 条映射 · 检查于 ${formatDate(overview.push.checked_at)}`)
    : '尚未配置密钥推送';
  // 签到到底给了多少，界面上原来只有"签到成功"这个布尔。各站分别给了多少直接
  // 写进副标题：一个站集体没签到时，这一行就是最先看出来的地方。用美元——
  // 原始额度是几亿的数，谁也不能一眼读出来那是多少钱。
  const quotaToday = overview.checkin_quota;
  const quotaBreakdown = (quotaToday?.sites ?? [])
    .map((site) => `${site.name} ${formatMoney(site.amount)}`)
    .join(' · ');
  const kpis = [
    {
      label: 'GitHub 账号', value: accounts.total, detail: `${siteLinkTotal} 个站点绑定`, icon: <GithubOutlined />, tone: 'blue', path: '/github/accounts',
    },
    {
      label: '正常账号', value: accountsHealthy, detail: `健康率 ${accountHealthPercent}%（全部绑定都正常）`, icon: <SafetyCertificateOutlined />, tone: 'green', path: '/github/accounts',
    },
    {
      // 封禁是按站发生的：一个号可能在 GoRouter 被封、在 Tabi 正常。卡片写的是
      // "账号"，所以给"至少有一个站被封的账号数"，不是被封的绑定数。
      label: '封禁账号',
      value: accounts.accounts_disabled ?? 0,
      detail: `${accounts.accounts_token_invalid ?? 0} 个账号令牌失效`,
      icon: <StopOutlined />,
      tone: (accounts.accounts_disabled ?? 0) ? 'red' : 'green',
      path: '/github/accounts',
    },
    {
      // 这张卡写的是"账号"，就必须给账号数。站点维度的 checked_in_today 在
      // 双站时是 76，而账号只有 38 —— 那个数只能叫"签到笔数"。
      // 关掉自动签到后待办清零，卡片改成说明开关状态，避免 0 待签看起来像全签完了。
      label: settings.checkin_enabled === false ? '自动签到' : '待签到账号',
      value: settings.checkin_enabled === false ? '已关闭' : (accounts.accounts_pending_checkin ?? 0),
      detail: settings.checkin_enabled === false
        ? `账号页仍可手动补签 · 今日已签 ${accounts.checked_in_today} 笔`
        : `今日已签到 ${accounts.accounts_checked_in_today ?? 0} 个账号 · 共 ${accounts.checked_in_today} 笔`,
      icon: <CalendarOutlined />,
      tone: settings.checkin_enabled === false
        ? 'slate'
        : (accounts.accounts_pending_checkin ?? 0) ? 'amber' : 'green',
      path: settings.checkin_enabled === false ? '/settings/monitor' : '/github/accounts',
    },
    {
      // 签到给了多少额度以前只存在于每个账号自己的快照里，而且隔天被覆盖。
      label: '今日签到收益',
      value: formatMoney(quotaToday?.amount),
      detail: quotaBreakdown || '今日还没有额度进账',
      icon: <GiftOutlined />,
      tone: (quotaToday?.amount ?? 0) ? 'green' : 'slate',
      path: '/checkin-quota',
    },
    {
      label: '总余额', value: `$${overview.total_balance.toFixed(2)}`, detail: '来自已保存账号快照', icon: <DollarOutlined />, tone: 'green', path: '/automation/websites',
    },
    {
      label: '开放注册站点', value: `${websites.registration_open}/${websites.total}`, detail: `${websites.registration_closed} 个已关闭注册`, icon: <GlobalOutlined />, tone: websites.registration_open ? 'green' : 'slate', path: '/automation/websites',
    },
    {
      // 这个数直接决定「批量补登站点」有多少活要干；原来仪表盘只说绑了多少，
      // 从不说还缺多少。
      label: '待补登站点',
      value: accounts.missing_links ?? 0,
      detail: '个账号×站点缺令牌',
      icon: <LinkOutlined />,
      tone: (accounts.missing_links ?? 0) ? 'amber' : 'green',
      path: '/github/accounts',
    },
    {
      // GitHub 封号原来在仪表盘上完全看不见：上面那个"封禁账号"说的是目标站。
      label: 'GitHub 异常',
      value: githubTroubleTotal,
      detail: githubTroubleDetail,
      icon: <GithubOutlined />,
      tone: githubTroubleTotal ? 'red' : 'green',
      path: '/github/accounts',
    },
    {
      // 余额原来只在"偏低"时以告警形式出现，平时看不到数；花费更是完全没有。
      label: '解算器余额',
      value: !solver?.configured
        ? '未启用'
        : solver.balance_unknown
          ? '读取失败'
          : `$${(solver.balance ?? 0).toFixed(2)}`,
      detail: solver?.configured
        ? (solver.balance_unknown
          ? '密钥可能已失效，需要 Turnstile 的站点会签到失败'
          : `昨日花费 $${(overview.solver_spend?.yesterday ?? 0).toFixed(4)}`)
        : '需要 Turnstile 的站点会签到失败',
      icon: <SafetyCertificateOutlined />,
      // 读不到余额不能显示绿色：原来只有 low_balance 一个布尔，读不到时它是
      // false，于是"密钥失效"和"余额健康"长得一模一样。
      tone: !solver?.configured
        ? 'slate'
        : solver.balance_unknown
          ? 'amber'
          : solver.low_balance ? 'red' : 'green',
      path: '/settings/solver',
    },
    {
      // 令牌失效是"推送悄悄不工作"的头号原因，以前只有真去推一次才知道。
      label: '推送令牌',
      value: pushTone.text,
      detail: pushDetail,
      icon: <KeyOutlined />,
      tone: pushTone.tone,
      path: '/automation/push-settings',
    },
  ];

  const scheduleRows = [
    { icon: <SafetyCertificateOutlined />, label: `账号测活 · 每 ${formatInterval(settings.health_check_interval_minutes)}`, next: schedule.next_health_run_at },
    { icon: <GlobalOutlined />, label: `注册探测 · 每 ${formatInterval(settings.registration_check_interval_minutes)}`, next: schedule.next_registration_run_at },
    { icon: <ClockCircleOutlined />, label: `版本探测 · 每 ${formatInterval(settings.version_check_interval_minutes)}`, next: schedule.next_version_run_at },
    {
      icon: <CalendarOutlined />,
      label: settings.checkin_enabled === false
        ? '自动签到 · 已关闭'
        : `自动签到 · ${settings.checkin_times.join('、') || '未设置'}`,
      next: settings.checkin_enabled === false ? null : schedule.next_checkin_run_at,
      nextText: settings.checkin_enabled === false ? '已关闭' : undefined,
    },
  ];

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div>
          <div className="dashboard-eyebrow">运行概览</div>
          <Title className="dashboard-title">账号与站点状态</Title>
          <Text type="secondary">集中查看账号健康、注册状态和下一轮巡检。</Text>
        </div>
        <Space className="dashboard-header-actions">
          <Text className="dashboard-updated">更新于 {formatDate(overview.generated_at)}</Text>
          <Button icon={<ReloadOutlined />} loading={refreshing} onClick={() => loadOverview(true)}>刷新</Button>
          <Button type="primary" icon={<SettingOutlined />} onClick={() => navigate('/settings/monitor')}>巡检设置</Button>
        </Space>
      </header>
      {/* 整站令牌集体失效放在最上面。这是"今天一整站都没签到"的唯一预警，
          以前它只体现为一堆账号各自变红，隔一天才会从签到结果被发现。 */}
      {(overview.site_token_alerts ?? []).map((alert) => (
        <Alert
          key={alert.origin}
          style={{ marginBottom: 16 }}
          type="error"
          showIcon
          message={`${alert.name} 的系统访问令牌集体失效（${alert.token_invalid}/${alert.links}）`}
          description={alert.message}
          action={
            <Button size="small" danger onClick={() => navigate('/github/accounts')}>
              去重新授权
            </Button>
          }
        />
      ))}
      {overview.solver?.configured && (overview.solver.low_balance || overview.solver.balance_error || overview.solver.balance_unknown) && (
        <Alert
          style={{ marginBottom: 16 }}
          type={overview.solver.balance_error ? 'error' : 'warning'}
          showIcon
          message={overview.solver.balance_error
            ? `解算器余额查询失败（${overview.solver.solver_type || '未知'}）`
            : overview.solver.balance_unknown
              ? `解算器余额还没读到（${overview.solver.solver_type || '未知'}）`
              : `解算器余额偏低：$${overview.solver.balance ?? '?'}`}
          description={overview.solver.balance_error
            ? `${overview.solver.balance_error}。余额查询失败不影响已有签到，但下一轮付费解算可能失败。`
            : overview.solver.balance_unknown
              ? '巡检还没成功读到余额。这不等于余额充足——密钥失效时看起来是同一个样子。'
              : `低于告警线 $${overview.solver.low_balance_threshold}，需要 Turnstile 的站点（如 Tabi）明天可能全部签到失败。检查于 ${formatDate(overview.solver.balance_checked_at || undefined)}。`}
        />
      )}

      <section className="dashboard-kpis" aria-label="关键指标">
        {kpis.map((item) => (
          <button type="button" className={`dashboard-kpi ${item.tone}`} key={item.label} onClick={() => navigate(item.path)}>
            <span className="dashboard-kpi-icon">{item.icon}</span>
            <span className="dashboard-kpi-copy">
              <span className="dashboard-kpi-label">{item.label}</span>
              <strong>{item.value}</strong>
              <span className="dashboard-kpi-detail">{item.detail}</span>
            </span>
          </button>
        ))}
      </section>

      <div className="dashboard-columns">
        <section className="dashboard-section">
          <div className="dashboard-section-heading">
            <div><Title level={4}>内置 API 网站</Title><Text type="secondary">版本和开放注册状态</Text></div>
            <Button type="link" onClick={() => navigate('/automation/websites')}>管理网站</Button>
          </div>
          <div className="site-status-list">
            {websites.items.length ? websites.items.map((site) => (
              <div className="site-status-row" key={site.id}>
                <div className="site-name"><span className="site-logo"><GlobalOutlined /></span><span><strong>{site.name}</strong><small>New API · {site.version || '版本未知'}</small></span></div>
                <div>{registrationTag(site)}</div>
                <div className="site-detection">最近探测 {formatDate(site.registration_checked_at || site.version_checked_at)}</div>
              </div>
            )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无内置网站" />}
          </div>
        </section>

        <section className="dashboard-section">
          <div className="dashboard-section-heading">
            <div><Title level={4}>巡检计划</Title><Text type="secondary">全局调度器，不创建用户级任务</Text></div>
            <Tag color="blue">{settings.timezone}</Tag>
          </div>
          <div className="schedule-list">
            {scheduleRows.map((row) => <div className="schedule-row" key={row.label}><span className="schedule-icon">{row.icon}</span><span className="schedule-label">{row.label}</span><span className="schedule-next">{row.nextText ?? `下次 ${formatDate(row.next)}`}</span></div>)}
          </div>
          <div className="schedule-next-run"><ClockCircleOutlined /> 总调度下次运行：<strong>{formatDate(schedule.next_run_at)}</strong></div>
        </section>
      </div>

      <section className="dashboard-section health-section">
        <div className="dashboard-section-heading">
          <div>
            <Title level={4}>目标站配额与出口 IP</Title>
            <Text type="secondary">
              关键请求按出口 IP 算配额，一个账号绑一个站要花 {' '}
              <strong>3-4 次</strong>；用满就得换 IP 或等窗口过去
            </Text>
          </div>
          <Button type="link" onClick={() => navigate('/settings/proxy')}>出口代理</Button>
        </div>
        <div className="site-status-list">
          {(overview.rate_limits || []).map((row) => {
            const used = row.limit ? Math.min(100, Math.round((row.spent / row.limit) * 100)) : 0;
            return (
              <div className="site-status-row" key={row.origin}>
                <div className="site-name">
                  <span className="site-logo"><GlobalOutlined /></span>
                  <span>
                    <strong>{row.name || row.origin}</strong>
                    <small>
                      已用 {row.spent}/{row.limit} 次
                      {row.margin ? `（上限 ${row.raw_limit} 留 ${row.margin} 余量）` : ''}
                      {' · '}窗口 {Math.round(row.window / 60)} 分钟
                    </small>
                  </span>
                </div>
                <div style={{ minWidth: 120 }}>
                  <Progress
                    percent={used}
                    size="small"
                    status={row.blocked_seconds ? 'exception' : used >= 100 ? 'active' : 'normal'}
                    strokeColor={used >= 100 ? '#cf1322' : used >= 60 ? '#faad14' : '#2f9e68'}
                  />
                </div>
                <div className="site-detection">
                  {row.blocked_seconds
                    ? `被限流，约 ${row.blocked_seconds} 秒后恢复`
                    : `还能跑 ${Math.floor(row.remaining / 4)} 个账号`}
                </div>
              </div>
            );
          })}
          {!(overview.rate_limits || []).length && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无内置站点" />
          )}
        </div>
        <div className="schedule-next-run">
          {overview.proxy?.enabled ? (
            <>
              <Tag color="green">出口代理已生效</Tag>
              撞配额自动换 IP：<strong>{overview.proxy.rotate_on_rate_limit ? '开' : '关'}</strong>
              {' · '}已换 <strong>{overview.proxy.rotations}</strong> 次
              {' · '}当前出口会话 <Text code>{overview.proxy.session_id}</Text>
              {overview.proxy.countries ? ` · ${overview.proxy.countries}` : ''}
              {!overview.proxy.apply_to_target && (
                <Tooltip title="代理开着，但没有勾选「目标站请求走代理」——429 就是目标站发的，这样等于没开">
                  <Tag color="red" style={{ marginLeft: 8 }}>未作用于目标站</Tag>
                </Tooltip>
              )}
            </>
          ) : (
            <>
              <Tag>未启用出口代理</Tag>
              配额用满只能等窗口过去（一次十几分钟）。配上住宅代理后撞墙时换 IP 就等于换一个空配额桶。
            </>
          )}
        </div>
      </section>

      <section className="dashboard-section health-section">
        <div className="dashboard-section-heading"><div><Title level={4}>账号健康</Title><Text type="secondary">最近一次协议探测汇总 · 状态按账号计，一个号只要有一个站有问题就算这个号有问题</Text></div><Button type="link" onClick={() => navigate('/github/accounts')}>查看账号</Button></div>
        <div className="health-overview">
          <div className="health-progress" title={`${accountsHealthy} / ${accounts.total} 个账号的全部站点都正常（共 ${siteLinkTotal} 个站点绑定，其中 ${accounts.healthy} 个正常）`}><Progress type="circle" percent={accountHealthPercent} strokeColor="#2f9e68" format={(percent) => <strong>{percent}%</strong>} /><span>账号健康率</span></div>
          <div className="health-stat-grid">
            <div title="全部绑定的站点都正常的账号数"><i className="status-dot green" /><strong>{accountsHealthy}</strong><span>正常账号</span></div>
            <div title="至少有一个站被目标站封禁的账号数。封禁是按站发生的，同一个号可能在另一个站完全正常"><i className="status-dot red" /><strong>{accounts.accounts_disabled ?? 0}</strong><span>有站被封</span></div>
            <div title="至少有一个站的系统访问令牌已失效的账号数"><i className="status-dot amber" /><strong>{accounts.accounts_token_invalid ?? 0}</strong><span>有站令牌失效</span></div>
            <div title="至少有一个站还没有系统访问令牌的账号数"><i className="status-dot slate" /><strong>{accounts.accounts_missing_token ?? 0}</strong><span>有站缺令牌</span></div>
            <div title="全部该签的站都签完了的账号数"><i className="status-dot blue" /><strong>{accounts.accounts_checked_in_today ?? 0}</strong><span>今日已签完</span></div>
            <div title={settings.checkin_enabled === false ? '自动签到已关闭，这些号还可以在账号页手动补签，但不算紧急待办' : '还有站没签到的账号数'}><i className="status-dot orange" /><strong>{accounts.accounts_pending_checkin ?? 0}</strong><span>{settings.checkin_enabled === false ? '待签（已关自动）' : '还有站待签'}</span></div>
            <div title="今天一共完成多少次签到。一个号绑两站就是两笔，所以这个数会大于账号数">
              <i className="status-dot blue" /><strong>{accounts.checked_in_today}</strong><span>签到笔数</span>
            </div>
            <div title="其中复用已保存的会话 Cookie 完成的笔数，未产生费用">
              <i className="status-dot green" /><strong>{accounts.checkin_via_cache ?? 0}</strong><span>其中缓存命中</span>
            </div>
            <div title="其中调用付费解算器完成的笔数。花费只在下面「今日解算花费」显示一处——快照里的花费每天被覆盖、每账号只留一笔，和日志汇总必然对不上，同页放两个数只会互相打脸">
              <i className="status-dot amber" /><strong>{accounts.checkin_via_solver ?? 0}</strong><span>其中付费解算</span>
            </div>
            {(accounts.checkin_via_unknown ?? 0) > 0 && (
              <div title="签到成功但快照里没有来源标记（旧数据）。有这个数才能保证三项之和等于签到笔数">
                <i className="status-dot slate" /><strong>{accounts.checkin_via_unknown}</strong><span>其中来源未知</span>
              </div>
            )}
            <div title="今日解算花费，按签到日（北京）从运行日志汇总">
              <i className="status-dot amber" /><strong>{formatMoney(overview.solver_spend?.today)}</strong><span>今日解算花费</span>
            </div>
            <div title="昨日解算花费。账号快照里的花费每天会被覆盖，这个数从运行日志重算">
              <i className="status-dot slate" /><strong>{formatMoney(overview.solver_spend?.yesterday)}</strong><span>昨日解算花费</span>
            </div>
            <div title="最近一次 OAuth 走纯协议完成的站点数，没有启动浏览器">
              <i className="status-dot green" /><strong>{accounts.login_via_protocol ?? 0}</strong><span>协议登录（按站）</span>
            </div>
            <div title="协议登录失败后回退到浏览器的站点数；这个数字越接近 0，Selenium 那条路就越可以退休">
              <i className="status-dot slate" /><strong>{accounts.login_via_browser ?? 0}</strong><span>浏览器回退（按站）</span>
            </div>
          </div>
          <div className="health-last-run"><Text type="secondary">最近测活</Text><strong>{formatDate(schedule.last_health_run_at)}</strong><Text type="secondary">最近签到</Text><strong>{formatDate(schedule.last_checkin_run_at)}</strong></div>
        </div>
      </section>

      <div className="dashboard-actions"><Button icon={<GithubOutlined />} onClick={() => navigate('/github/accounts')}>账号管理</Button><Button icon={<KeyOutlined />} onClick={() => navigate('/automation/websites')}>API 网站</Button><Button icon={<CheckCircleOutlined />} onClick={() => navigate('/automation/push-settings')}>密钥推送</Button></div>
    </div>
  );
};

export default Dashboard;
